<template>
  <v-card height="660px">
    <v-toolbar dark color="secondary">
      <v-toolbar-title> Notes for {{ clientName }} </v-toolbar-title>
      <v-spacer />
      <v-btn icon dark @click="closeNotes()">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>
    <v-card-text class="max-height py-0">
      <v-row class="pt-2 full-height">
        <v-col class="full-height overflow-auto">
          <div class="text-center ma-12" v-if="loading">
            <v-progress-circular :indeterminate="loading" color="blue" :size="50" />
          </div>
          <div v-for="(note, index) in notes" :key="note.id">
            <note-card
              v-if="!note.reminderDate"
              :note="note"
              :index="index"
              :active="form.data.id && note.id == form.data.id"
              @try-edit-note="tryEditNote(note, index)"
              @edit-note="editNote(index)"
              @delete-note="deleteNote(note.id)"
            />
            <reminder-card
              v-else
              :note="note"
              :index="index"
              :active="form.data.id && note.id == form.data.id"
              @try-edit-note="tryEditNote(note, index, 1)"
              @edit-note="editNote(index, 1)"
              @delete-note="deleteNote(note.id)"
            />
          </div>
        </v-col>
        <v-col cols="4" class="full-height">
          <v-card>
            <v-card-title class="pr-1">
              {{ editCardTitle }}
              <v-btn-toggle v-model="noteType" dense mandatory class="d-flex ml-auto">
                <v-btn icon>
                  <v-icon color="navy">
                    mdi-notebook
                  </v-icon>
                </v-btn>
                <v-btn icon>
                  <v-icon color="green">
                    mdi-calendar
                  </v-icon>
                </v-btn>
              </v-btn-toggle>
            </v-card-title>
            <v-card-text>
              <v-form>
                <v-select
                  v-if="isNote"
                  v-model="form.data.importance"
                  :items="form.importance"
                  label="Importance"
                  dense
                />
                <v-text-field ref="formTitle" v-model="form.data.title" label="Title" />
                <v-select
                  v-if="isReminder"
                  v-model="form.data.reminderType"
                  :items="form.reminderType"
                  label="Reminder type"
                  dense
                />
                <v-textarea :rows="isNote ? 10 : 8" no-resize v-model="form.data.note" label="Note" dense />
                <v-menu
                  v-if="isReminder"
                  ref="dateMenu"
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  required
                  min-width="130px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.data.reminderDate"
                      v-bind="attrs"
                      v-on="on"
                      label="Reminder date"
                      prepend-icon="mdi-calendar"
                      readonly
                      clearable
                      required
                      dense
                    />
                  </template>

                  <v-date-picker ref="picker" v-model="form.data.reminderDate" min="1920-01-01" @change="setDate" />
                </v-menu>
                <v-card-actions>
                  <v-btn @click="cancelNote">Cancel</v-btn>
                  <v-btn @click="saveNote" class="ml-auto" color="secondary">Save</v-btn>
                </v-card-actions>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  USER_NOTES_QUERY,
  HUBSPOT_NOTES_QUERY,
  USER_NOTE_UPSERT_QUERY,
  USER_NOTE_DELETE_QUERY,
} from "@/constants/graphql/coach/notes";
import NoteCard from "@/components/dashboard/components/notes/NoteCard";
import ReminderCard from "@/components/dashboard/components/notes/ReminderCard";
import { capitalizeAllWords } from "@/lib/string";

export default {
  name: "UserNotes",
  props: {
    clientName: {
      type: String,
      required: false,
    },
    ticketId: {
      type: String,
      default: null,
    },
    coachId: {
      type: String,
      default: null,
    },
    userId: {
      type: String,
      default: null,
    },
  },
  components: {
    NoteCard,
    ReminderCard,
  },
  data() {
    return {
      loading: true,
      userNotes: null,
      hubspotNotes: null,
      notes: [],
      noteType: 0,
      problemConnecting: false,
      dateMenu: null,
      form: {
        data: {
          id: null,
          importance: "Info",
          title: "",
          note: "",
          reminderDate: null,
          reminderType: null,
        },
        importance: [
          { text: "Note", value: "Info" },
          { text: "Warning", value: "Warning" },
          { text: "Urgent", value: "Urgent" },
          { text: "Sticky", value: "Sticky" },
        ],
        reminderType: [
          { text: "Follow Up", value: "FOLLOW_UP" },
          { text: "Reminder", value: "REMINDER" },
        ],
      },
    };
  },
  computed: {
    selectedUserId() {
      return this.userId || this.program.user.id;
    },
    editCardTitle() {
      let name = this.isNote ? "Note" : "Reminder Note";
      return this.form.data.id == null ? `New ${name}` : `Edit ${name}`;
    },
    isNote() {
      return this.noteType == 0;
    },
    isReminder() {
      return this.noteType == 1;
    },
    invalid() {
      return this.isReminder && this.form.data.reminderDate == null;
    },
  },
  methods: {
    resetForm() {
      this.form.data = {
        id: null,
        importance: "Info",
        title: "",
        note: "",
        reminderType: null,
        reminderDate: null,
      };
    },
    setDate(date) {
      this.$refs.dateMenu.save(date);
    },
    noteChanged() {
      if (this.form.data.id) {
        let note = this.notes.find((note) => note.id == this.form.data.id);
        return (
          note.title != this.form.data.title ||
          note.note != this.form.data.note ||
          note.reminderDate != this.form.data.reminderDate
        );
      } else {
        return this.form.data.title || this.form.data.note;
      }
    },
    closeNotes() {
      if (this.form.data.id == null && (this.form.data.title || this.form.data.note)) {
        let confirmed = confirm("Are you sure you want to lose this new note?");
        if (!confirmed) return;
      } else if (this.form.data.id) {
        if (this.noteChanged()) {
          let confirmed = confirm("Are you sure you want to lose your changes?");
          if (!confirmed) return;
        }
      }

      this.$emit("close");
    },
    cancelNote() {
      if (this.form.data.id == null && (this.form.data.title || this.form.data.note)) {
        let confirmed = confirm("Are you sure you want to lose this new note?");
        if (!confirmed) return;
      } else if (this.form.data.id) {
        if (this.noteChanged()) {
          let confirmed = confirm("Are you sure you want to lose your changes?");
          if (!confirmed) return;
        }
      }
      this.resetForm();
    },
    tryEditNote(note, index, noteType = 0) {
      if (!note.hubspot) {
        this.editNote(index, noteType);
      }
    },
    editNote(index, noteType = 0) {
      let note = this.notes[index];

      if (this.noteChanged()) {
        let confirmed = confirm("Are you sure you want to lose your changes?");
        if (!confirmed) return;
      }

      let keys = ["id", "importance", "title", "note", "reminderDate", "reminderType"];
      for (let idx in keys) {
        let val = note[keys[idx]];
        if (["importance"].includes(keys[idx])) {
          val = capitalizeAllWords(val);
        }
        this.form.data[keys[idx]] = val;
      }
      this.noteType = noteType;
      this.$refs.formTitle.focus();
    },
    deleteNote(noteId) {
      let confirmed = confirm("Are you sure you want to delete this note?");
      if (!confirmed) return;

      if (noteId == this.form.data.id) {
        this.resetForm();
      }

      this.$apollo
        .mutate({
          mutation: USER_NOTE_DELETE_QUERY,
          variables: { id: noteId },
          refetchQueries: ["userSubscriptions", "userNotes", "clientHistoryQuery", "coachDashboardListing"],
        })
        .then((result) => {
          this.$toasted.success("Note Deleted", {
            duration: 2000,
            position: "bottom-center",
          });
        })
        .catch((error) => {
          this.$emit("problem");
          this.problemConnecting = true;
        });
    },
    saveNote() {
      if (this.invalid) {
        confirm("Missing reminder date");
      } else {
        let variables = {
          input: {
            userProgramId: this.program && this.program.id,
            title: this.form.data.title,
            note: this.form.data.note,
            userId: this.selectedUserId,
            importance: this.form.data.importance,
            reminderType: this.form.data.reminderType,
            reminderDate: this.form.data.reminderDate,
          },
        };

        if (this.form.data.id != null) variables.input.id = this.form.data.id;

        this.$apollo
          .mutate({
            mutation: USER_NOTE_UPSERT_QUERY,
            variables: variables,
            refetchQueries: ["userSubscriptions", "userNotes", "clientHistoryQuery", "coachDashboardListing"],
          })
          .then((result) => {
            let message = typeof variables.input.id == "undefined" ? "Note Added" : "Note Updated";
            this.$toasted.success(message, {
              duration: 2000,
              position: "bottom-center",
            });
            this.resetForm();
          })
          .catch((error) => {
            this.$emit("problem");
            this.problemConnecting = true;
          });
      }
    },
    setNotes() {
      if (this.userNotes != null && (this.hubspotNotes != null || this.ticketId == null)) {
        let notes = this.userNotes;

        if (this.ticketId != null) {
          notes = notes.concat(this.hubspotNotes);
        }

        if (notes.length > 0) {
          notes.sort((a, b) => (a.sort < b.sort ? 1 : -1));
          this.notes = notes;
        }

        this.loading = false;
      }
    },
  },
  apollo: {
    userNotes: {
      query: USER_NOTES_QUERY,
      variables() {
        return {
          userId: parseInt(this.selectedUserId),
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data) {
          this.userNotes = [];

          response.data.userNotes.forEach((note) => {
            this.userNotes.push({
              id: note.id,
              hubspot: false,
              coach: note.coach.id != this.$store.userId ? `${note.coach.firstName} ${note.coach.lastName}` : "",
              importance: note.importance,
              note: note.note,
              title: note.title,
              assessment: {
                id: note.assessment?.id ?? null,
                number: note.assessment?.number ?? null,
                date: note.assessment?.requiredDate ?? null,
              },
              reminderType: note.reminderType,
              reminderDate: note.reminderDate,
              reminderCompleted: note.reminderCompleted || false,
              createdAt: this.$moment(note.createdAt, "YYYY-MM-DD HH:mm:ss"),
              sort: note.reminderDate
                ? this.$moment(note.reminderDate, "YYYY-MM-DD HH:mm:ss")
                : this.$moment(note.createdAt, "YYYY-MM-DD HH:mm:ss"),
            });
          });

          this.setNotes();
        }
      },
    },
    hubspotNotes: {
      query: HUBSPOT_NOTES_QUERY,
      variables() {
        return {
          ticketId: this.ticketId,
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data) {
          this.hubspotNotes = [];
          response.data.hubspotNotes.forEach((note) => {
            this.hubspotNotes.push({
              id: null,
              hubspot: true,
              coach: note.source,
              importance: null,
              note: note.body,
              title: null,
              assessment: null,
              createdAt: this.$moment(note.createdAt, "YYYY-MM-DD HH:mm:ss"),
              sort: this.$moment(note.createdAt, "YYYY-MM-DD HH:mm:ss"),
            });
          });

          this.setNotes();
        }
      },
      skip() {
        return this.ticketId == null;
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.max-height {
  height: calc(100% - 64px);
}
.full-height {
  height: 100%;
}
</style>
